import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { ConfirmDialog, type ConfirmOptions } from "@gui/widgets/ConfirmDialog";
import type { ConfirmFn } from "./ConfirmContext.types";
import { useConfirmProviderViewModel } from "./ConfirmContext.vm";

/** Outside a provider nothing can be shown, so the answer is no: the guarded action does not run. */
const confirmWithoutProvider: ConfirmFn = () => {
	console.error("useConfirm() was called outside a <ConfirmProvider>; the confirmation was declined.");
	return Promise.resolve(false);
};

/** The value is the bare `confirm` function, which never changes, so asking re-renders no consumer. */
export const ConfirmContext = createContext<ConfirmFn>(confirmWithoutProvider);

/** Holds the app's one confirm dialog (mounted innermost in AppProviders). */
export function ConfirmProvider(props: { children: React.ReactNode }) {
	const vm = useConfirmProviderViewModel();
	return (
		<ConfirmContext.Provider value={vm.confirm}>
			{props.children}
			{vm.dialog && <ConfirmDialog {...vm.dialog} />}
		</ConfirmContext.Provider>
	);
}

/**
 * Asks before an action, in place of the browser's blocking `confirm()`:
 * `if (!(await confirm({ title, body, confirmLabel, tone }))) return;`
 *
 * A request still open when the calling component unmounts resolves `false` and its dialog closes,
 * so a route change or reset never runs a stale action.
 */
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
	const confirm = useContext(ConfirmContext);
	const controller = useRef<AbortController | null>(null);
	useEffect(() => {
		// Created in the effect body (not only cleared in the cleanup): StrictMode runs
		// mount → cleanup → mount, and the second mount needs a live controller.
		const current = new AbortController();
		controller.current = current;
		return () => current.abort();
	}, []);
	return useCallback((options: ConfirmOptions) => confirm(options, controller.current?.signal), [confirm]);
}
